import React, { useContext, useRef, useMemo, useState, useEffect } from "react"

import { useUser } from 'store'
import Image from './SVGs'

const MapContext = React.createContext({})

const computeRatio = (size, sizeLimit) => {
  if (size?.length > 1) {
    const ratioX = sizeLimit[0] / size[0]
    const ratioY = sizeLimit[1] / size[1]
    return Math.min(ratioX, ratioY, 1)
  }
  return 1
}

export const useMap = () => useContext(MapContext)

const MapProvider = ({
  popupAnchorRef,
  transformRef,
  showSvg = false,
  transformWrapperState,
  image,
  grayscale,
  iconSize,
  setTransform,
  hasOpenCard = false,
  children
}) => {
  const mapEl = useRef(null)
  const svgEl = useRef(null)
  const [sizeLimit, setSizeLimit] = useState([1000, 600])
  const [dimensionKey, setDimensionKey] = useState(0)
  const [user] = useUser()
  useEffect(() => {
    if (!transformRef.current) return
    const updateSizeLimit = () => {
      const width = Math.max(transformRef.current.clientWidth - 40, 100)
      const height = Math.max(transformRef.current.clientHeight - 40, 100)
      setSizeLimit([width, height])
      setDimensionKey(prev => prev + 1)
    }
    updateSizeLimit()
    const observer = new ResizeObserver(updateSizeLimit)
    observer.observe(transformRef.current)
    return () => observer.disconnect()
  }, [transformRef])

  const ratio = useMemo(() =>
    computeRatio([image.width, image.height], sizeLimit),
    [image, sizeLimit]
  )

  const imageSize = useMemo(() => [image.width * ratio, image.height * ratio], [image, ratio])

  const convertPosToMapBase = ({position}) => {
    return {
      x: position.x * ratio > imageSize[0]
        ? imageSize[0]
        : (position.x * ratio),
      y: position.y * ratio > imageSize[1]
        ? imageSize[1]
        : position.y * ratio
    }
  }

  const convertPosRealToMap = ({position, hasChip = true}) => {
    const {scale} = transformWrapperState
    return {
      x: position.x * ratio > imageSize[0]
        ? imageSize[0] - (( iconSize / scale ) * 2)
        : (position.x * ratio) - ((iconSize / scale) / 2),
      y: position.y * ratio > imageSize[1]
        ? imageSize[1] - ( (iconSize / scale) * 2 )
        : (position.y * ratio) - (hasChip ? (iconSize + iconSize / 4) / scale : iconSize / scale) / 2
    }
  }

  const centerOnMarker = (markerPosition) => {
    if (!transformRef.current || !setTransform || !markerPosition) return
    if (typeof markerPosition.x !== 'number' || typeof markerPosition.y !== 'number') return

    const { scale, positionY } = transformWrapperState
    
    const markerMapX = markerPosition.x * ratio
    const markerMapY = markerPosition.y * ratio
    
    const CARD_WIDTH = hasOpenCard ? 0 : 200
    const centerOffsetX = CARD_WIDTH / 2
    
    const targetPositionX = (transformRef.current.clientWidth / 2) - (markerMapX * scale) - centerOffsetX
    
    const markerScreenY = (markerMapY * scale) + positionY
    const SCREEN_MARGIN = 50
    
    let targetPositionY = positionY
    if (markerScreenY < SCREEN_MARGIN) {
      targetPositionY = SCREEN_MARGIN - (markerMapY * scale)
    } else if (markerScreenY > transformRef.current.clientHeight - SCREEN_MARGIN) {
      targetPositionY = (transformRef.current.clientHeight - SCREEN_MARGIN) - (markerMapY * scale)
    }
    setTransform(targetPositionX, targetPositionY, scale, 300)
  }

  return (
    <MapContext.Provider value={{
      popupAnchorRef,
      imageSize,
      iconSize,
      sizeLimit,
      mapEl,
      svgEl,
      ratio,
      transformRef,
      transformWrapperState,
      centerOnMarker,
      convertPosRealToMap: (position) => convertPosRealToMap({position}),
      convertPosToMapBase: (position) => convertPosToMapBase({position})
    }}>
      {
        !showSvg
          ? children
          : (
            <svg
              x={0}
              y={0}
              width={imageSize[0]}
              height={imageSize[1]}
              viewBox={`0 0 ${imageSize[0]} ${imageSize[1]}`}
              ref={svgEl}
              style={{
                boxShadow: `
            0px 1.85px 3.15px 0px rgba(0, 0, 0, 0.08),
            0px 8.15px 6.52px 0px rgba(0, 0, 0, 0.04),
            0px 20px 13px 0px rgba(0, 0, 0, 0.04),
            0px 38.52px 25.48px 0px rgba(0, 0, 0, 0.043),
            0px 64.81px 46.85px 0px rgba(0, 0, 0, 0.055)
          `
              }}
            >
              <rect x={0} y={0} width={imageSize[0]} height={imageSize[1]} fill={'#fbfbfb'} />
              <Image
                ref={mapEl}
                size={[imageSize[0], imageSize[1]]}
                dataURL={image.dataURL}
                grayscale={grayscale}
                blur={Boolean(user?.demo_mode)}
              />
              <g key={dimensionKey}>
                {children}
              </g>
            </svg>
          )
      }
    </MapContext.Provider>
  )
}

export default MapProvider